import React, { useEffect, useState } from "react";
import {
  Pie,
  PieChart,
  Cell,
  PieLabelRenderProps,
  Sector,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/uiComponents/components/ui/card";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { graphOrderApi } from "@/modules/organizationServices";

import { AggregatedCategoryEntry } from "@/types/chart";

import { orderByCategory } from "@/store/redux/organization/graphExpenses";

import { PieSectorDataItem } from "recharts/types/polar/Pie";
import { ErrorHelper } from "@/utils/errorHelper";
import { toastMessage } from "@/utils/toastHelper";

const renderActiveShape = (props: PieSectorDataItem) => {
  const {
    cx,
    cy,

    outerRadius,
    startAngle,
    endAngle,
    fill,
  } = props;

  return (
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={70}
      outerRadius={outerRadius ? outerRadius + 10 : 110}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  );
};

function generateColor(index: number): string {
  const hue = (index * 137.508) % 360; 
  return `hsl(${hue}, 70%, 60%)`;
}


const COLOR_PALETTE = [
  "#65baf3",
  "#5ba7db",
  "#5195c2",
  "#4782aa",
  "#3d7092",
  "#335d7a",
  "#284a61",
  "#1e3849",
  "#142531",
  "#0a1318",
  "#74c1f4",
  "#84c8f5",
  "#93cff7",
  "#a3d6f8",
  "#b2ddf9",
  "#c1e3fa",
  "#d1eafb",
  "#e0f1fd"
];


const getColorFromPalette = (index: number): string => {
  if (index < COLOR_PALETTE.length) {
    return COLOR_PALETTE[index];
  } else {
    return generateColor(index);
  }
};

export function PieChartCategory() {
  const dispatch = useDispatch();
  const filter = useSelector((state: RootState) => state.filter.filter);
  const expenses = useSelector(
    (state: RootState) => state.graph.sortedByCategory,
  );

  // const [chartConfig, setChartConfig] = useState<ChartConfig>({});
  const [aggregatedData, setAggregatedData] = useState<
    AggregatedCategoryEntry[]
  >([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const handlePieEnter = (_: PieLabelRenderProps, index: number) => {
    setActiveIndex(index);
  };

  const handlePieLeave = () => {
    setActiveIndex(null);
  };

  const getExpensesByCategory = async () => {
    try {
      const orderByFilter = {
        ...filter,
        groupBy: "category",
      };
      const response = await graphOrderApi(orderByFilter);

      if (response.labels != undefined && response.values != undefined) {
        dispatch(
          orderByCategory({ labels: response.labels, values: response.values }),
        );
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  useEffect(() => {
    getExpensesByCategory();
  }, [filter]);

  type ChartConfiguration = Record<
    string,
    { label: string; color: string; amount: number }
  >;
  type CategoryTotals = Record<string, number>;
  useEffect(() => {
    const newChartConfig: ChartConfiguration = {};
    const categoryTotals: CategoryTotals = {};

    expenses.forEach((expense) => {
      expense.labels.forEach((label, index) => {
        const categoryKey = label;
        const amount = expense.values[index];

        // Initialize chart config if needed
        if (!newChartConfig[categoryKey]) {
          newChartConfig[categoryKey] = {
            label: categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1),
            color: getColorFromPalette(Object.keys(newChartConfig).length),
            amount: 0,
          };
        }

        if (!isNaN(amount)) {
          categoryTotals[categoryKey] = amount;
        }
      });
    });

    // Set the final chart config
    Object.entries(categoryTotals).forEach(([categoryKey, total]) => {
      if (newChartConfig[categoryKey]) {
        newChartConfig[categoryKey].amount = total;
      }
    });

    // setChartConfig(newChartConfig);

    const formattedData = Object.entries(categoryTotals).map(
      ([category, amount]) => ({
        category,
        amount,
        fill: newChartConfig[category]?.color || "#ccc",
      }),
    );

    setAggregatedData(formattedData);
  }, [expenses]);

  const totalAmount = aggregatedData
    .reduce((sum, entry) => sum + entry.amount, 0)
    .toFixed(2);

  return (
    <Card className="flex flex-1 flex-col relative sm:w-[38vw] mt-3 md:mt-0 md:ml-4">
      <CardHeader className="items-center pb-0">
        <CardTitle>Expenses By Category</CardTitle>
      </CardHeader>
      {aggregatedData.length == 0 ? (
        <div className="text-center mt-10 text-gray-500"> No expenses to show</div>
      ) : (
        <CardContent className="flex-1 pb-0">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Chart Container */}
            <div className="w-full md:w-2/3 h-[300px] relative">
              <ResponsiveContainer height="100%">
                <PieChart>
                  <Tooltip
                    formatter={(value: number) => [`Rs. ${value}`, "Amount"]}
                  />
                  <Pie
                    data={aggregatedData}
                    dataKey="amount"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={70}
                    activeIndex={activeIndex !== null ? activeIndex : undefined}
                    activeShape={renderActiveShape}
                    onMouseEnter={handlePieEnter}
                    onMouseLeave={handlePieLeave}
                  >
                    {aggregatedData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.fill}
                        stroke="#fff"
                        strokeWidth={0}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              {/* Centered total inside the donut */}
              <div className="absolute  top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
                <p className="text-sm text-gray-600 ">Total</p>
                <p className="text-lg font-semibold text-black">
                  Rs. {totalAmount}
                </p>
              </div>
            </div>

            {/* Legend Container */}
            <div className="w-full md:w-1/5">
              <div className="flex flex-col gap-2">
                {aggregatedData.map((entry, index) => (
                  <div
                    key={index}
                    className={`flex align-center  gap-2  ${
                      activeIndex === index
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(null)}
                  >
                    <div
                      className="w-3 h-3 mt-1 rounded-xs"
                      style={{ backgroundColor: entry.fill }}
                    />
                    <span className="text-sm capitalize  text-black">
                      {entry.category}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
